/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Bilingual Dual-Column Segment Row Component (Task 10.5.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useEffect } from 'react';
import {
  Check,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Database,
  CornerDownLeft,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { CatSegment } from '../../stores/useCatEditorStore';
import { InlineTagRenderer } from './InlineTagRenderer';

interface DualColumnRowProps {
  segment: CatSegment;
  isActive: boolean;
  onActivate: (id: string) => void;
  onUpdateTarget: (id: string, text: string) => void;
  onCommit: (id: string) => void;
  onApprove: (id: string) => void;
  onApplySuggestion: (id: string, type: 'tm' | 'mt') => void;
  onNavigate: (direction: 'next' | 'prev') => void;
}

export const DualColumnRow: React.FC<DualColumnRowProps> = ({
  segment,
  isActive,
  onActivate,
  onUpdateTarget,
  onCommit,
  onApprove,
  onApplySuggestion,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Focus textarea when row becomes active
  useEffect(() => {
    if (isActive && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isActive]);

  // Auto-resize textarea height to content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(64, textareaRef.current.scrollHeight)}px`;
    }
  }, [segment.targetText]);

  // Handle keyboard shortcuts within target editor
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;

    // 1. Ctrl + Enter -> Commit & Next
    if (isCtrlOrCmd && e.key === 'Enter') {
      e.preventDefault();
      onCommit(segment.id);
      return;
    }

    // 2. Ctrl + Down -> Next segment
    if (isCtrlOrCmd && e.key === 'ArrowDown') {
      e.preventDefault();
      onNavigate('next');
      return;
    }

    // 3. Ctrl + Up -> Previous segment
    if (isCtrlOrCmd && e.key === 'ArrowUp') {
      e.preventDefault();
      onNavigate('prev');
      return;
    }

    // 4. Ctrl + 1 -> Insert TM match
    if (isCtrlOrCmd && e.key === '1') {
      e.preventDefault();
      onApplySuggestion(segment.id, 'tm');
      return;
    }

    // 5. Ctrl + 2 -> Insert MT match
    if (isCtrlOrCmd && e.key === '2') {
      e.preventDefault();
      onApplySuggestion(segment.id, 'mt');
      return;
    }
  };

  const handleInsertTag = (tag: string) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart || segment.targetText.length;
    const end = textareaRef.current.selectionEnd || segment.targetText.length;
    const newText =
      segment.targetText.slice(0, start) + tag + segment.targetText.slice(end);
    onUpdateTarget(segment.id, newText);

    // Reposition cursor after inserted tag
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(start + tag.length, start + tag.length);
      }
    }, 10);
  };

  const isApproved = segment.status === 'approved';
  const isDraft = segment.status === 'draft';
  const isUntranslated = segment.status === 'untranslated';
  const hasWarnings = segment.qaWarnings.length > 0;

  return (
    <div
      onClick={() => onActivate(segment.id)}
      className={`group relative grid grid-cols-1 md:grid-cols-12 gap-0 border-b transition-all duration-150 ${
        isActive
          ? 'bg-primary/[0.04] border-primary/40 shadow-sm'
          : 'bg-surface hover:bg-surface-elevated/40 border-surface-border'
      }`}
    >
      {/* Active Indicator Bar */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 transition-colors ${
          isActive ? 'bg-primary' : isApproved ? 'bg-emerald-500/60' : 'bg-transparent'
        }`}
      />

      {/* 1. LEFT COLUMN: Source Segment (Col span 5) */}
      <div className="md:col-span-5 p-4 pl-5 border-r border-surface-border/80 flex flex-col justify-between space-y-3">
        <div className="space-y-2">
          {/* Row Number & Word Count Header */}
          <div className="flex items-center justify-between text-[11px] font-mono text-foreground-muted select-none">
            <span className="font-semibold text-primary">#{segment.chunkIndex}</span>
            <span>
              {segment.wordCount} words • {segment.charCount} chars
            </span>
          </div>

          {/* Protected Source Segment Text with Inline Tag Rendering */}
          <div className="text-sm text-foreground font-sans leading-relaxed select-text">
            <InlineTagRenderer text={segment.sourceText} onTagClick={handleInsertTag} />
          </div>
        </div>

        {/* Suggestion hints summary */}
        {segment.tmSuggestion && (
          <div className="pt-2 border-t border-surface-border/50 flex items-center justify-between text-[10px] text-foreground-muted">
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3 text-emerald-500" />
              <span>TM: {segment.tmSuggestion.matchPercent}% Match</span>
            </span>
            <span className="font-mono text-[9px] bg-surface-elevated px-1.5 py-0.5 rounded border border-surface-border">
              Ctrl+1
            </span>
          </div>
        )}
      </div>

      {/* 2. MIDDLE COLUMN: Status & Quality Telemetry (Col span 1) */}
      <div className="md:col-span-1 p-3 border-r border-surface-border/80 flex flex-row md:flex-col items-center justify-between md:justify-center gap-2 select-none">
        {/* Status Badge */}
        {isApproved && (
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/15 text-emerald-500 border border-emerald-500/30" title={t('cat.statusApproved')}>
            <CheckCircle2 className="w-4 h-4" />
          </span>
        )}
        {isDraft && (
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary/15 text-primary border border-primary/20">
            {t('cat.statusDraft')}
          </span>
        )}
        {isUntranslated && (
          <span className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-medium bg-surface-elevated text-foreground-muted border border-surface-border">
            {t('cat.statusUntranslated')}
          </span>
        )}

        {/* TM match score */}
        {segment.tmMatchPercent > 0 && (
          <span
            className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
              segment.tmMatchPercent === 100
                ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/20'
                : 'bg-amber-500/15 text-amber-500 border border-amber-500/20'
            }`}
            title={`Translation Memory Match: ${segment.tmMatchPercent}%`}
          >
            {segment.tmMatchPercent}%
          </span>
        )}

        {/* QA Warnings alert */}
        {hasWarnings && (
          <div
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-rose-500/15 text-rose-500 cursor-help"
            title={segment.qaWarnings.join('\n')}
          >
            <AlertTriangle className="w-3.5 h-3.5" />
          </div>
        )}
      </div>

      {/* 3. RIGHT COLUMN: Target Segment Translation Editor (Col span 6) */}
      <div className="md:col-span-6 p-4 flex flex-col justify-between space-y-3">
        <div className="space-y-2">
          {/* Target Textarea Editor */}
          <textarea
            ref={textareaRef}
            rows={2}
            spellCheck="true"
            value={segment.targetText}
            onChange={(e) => onUpdateTarget(segment.id, e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('cat.targetPlaceholder')}
            className={`w-full p-2.5 rounded-xl border text-sm text-foreground bg-surface font-sans resize-none transition-all leading-relaxed focus:outline-none ${
              isActive
                ? 'border-primary ring-1 ring-primary/40 shadow-sm'
                : 'border-surface-border hover:border-surface-border/80'
            }`}
          />

          {/* QA Warnings Banner if any */}
          {hasWarnings && (
            <div className="p-2 rounded-lg bg-rose-500/10 border border-rose-500/20 flex flex-col gap-1 text-[11px] text-rose-400">
              {segment.qaWarnings.map((warn, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                  <span>{warn}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Toolbar for Active Row */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-surface-border/40 text-[11px]">
          <div className="flex items-center gap-1.5">
            {segment.tmSuggestion && (
              <button
                type="button"
                onClick={() => onApplySuggestion(segment.id, 'tm')}
                className="px-2 py-1 rounded-lg border border-emerald-500/30 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 font-medium text-[10px] flex items-center gap-1 transition-colors"
                title={t('cat.shortcutInsertTmDesc')}
              >
                <Database className="w-3 h-3" />
                <span>TM ({segment.tmSuggestion.matchPercent}%)</span>
              </button>
            )}

            {segment.mtSuggestion && (
              <button
                type="button"
                onClick={() => onApplySuggestion(segment.id, 'mt')}
                className="px-2 py-1 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary font-medium text-[10px] flex items-center gap-1 transition-colors"
                title={t('cat.shortcutInsertMtDesc')}
              >
                <Sparkles className="w-3 h-3" />
                <span>MT ({segment.mtSuggestion.provider})</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-foreground-muted font-mono hidden sm:inline-block">
              {segment.targetText.length} chars
            </span>

            <button
              type="button"
              onClick={() => onApprove(segment.id)}
              className={`p-1.5 rounded-lg border text-xs font-semibold flex items-center gap-1 transition-colors ${
                isApproved
                  ? 'border-emerald-500/40 bg-emerald-500/20 text-emerald-500'
                  : 'border-surface-border hover:border-emerald-500/40 hover:text-emerald-500 text-foreground-muted'
              }`}
              title={t('cat.approveSegment')}
            >
              <Check className="w-3.5 h-3.5" />
            </button>

            <button
              type="button"
              onClick={() => onCommit(segment.id)}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-fg font-semibold text-xs shadow-sm hover:opacity-95 flex items-center gap-1.5 transition-all"
              title={t('cat.commitSegment')}
            >
              <span>{t('cat.saveAndNext')}</span>
              <CornerDownLeft className="w-3 h-3 opacity-80" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
